import * as React from 'react'
import Input from '@frontrow/console/src/entities/components/base/input'
import {sortedCountries} from '@frontrow/console/src/entities/helpers/countries'
import {mmap} from '@freckle/maybe'
import {cn} from '@frontrow/console/src/lib/utils'
import {INPUT_BASE} from '@frontrow/console/src/lib/form-styles'

import FormField from '@frontrow/console/src/components/common/form-field'
import {type AddressT} from '@frontrow/console/src/helpers/addresses'
import {
  usStates,
  stateFromAdministrativeArea,
  zipCodeFromPostalCode
} from '@frontrow/console/src/helpers/addresses'

type Props = {
  isNewAddress: boolean
  address: AddressT
  onChangeAddress: (address: AddressT) => void
  disabled?: boolean
  // Rendered in the other half of the formatted-address row. Without it the
  // textarea keeps the whole row.
  aside?: React.ReactNode
}

type PlaceholdersT = {
  city: string
  administrativeArea: string
  postalCode: string
  addressText: string
}

function ifUSA<A>(countryCode?: string | null): (usa: A, notUSA: A) => A {
  return (usa, notUSA) => (countryCode === 'USA' ? usa : notUSA)
}

function getCreatePlaceholders(countryCode?: string | null): PlaceholdersT {
  const pick = ifUSA(countryCode)
  return {
    city: pick('San Francisco', 'Dublin') as string,
    administrativeArea: pick('CA', 'Co. Dublin') as string,
    postalCode: pick('94103', 'Dublin 8') as string,
    addressText: pick(
      '100 Bush Street\nSan Francisco, CA 94104',
      'Whitefriar Street\nDublin 8'
    ) as string
  }
}

function getUpdatePlaceholders(countryCode?: string | null): PlaceholdersT {
  const pick = ifUSA(countryCode)
  return {
    name: 'Name is required',
    city: 'City is required',
    administrativeArea: pick(
      'State is required',
      'Administrative area (e.g. state or province) is required'
    ) as string,
    postalCode: pick('5-digit zip is required', 'Postal code is required') as string,
    addressText: 'Formatted address is required'
  } as PlaceholdersT
}

// The address block owns its grid rather than inheriting one, so a caller cannot
// change how the address reads by changing its own layout.
//
// The spans below are container queries rather than viewport breakpoints, which
// is what lets both callers share one set of classes: each row arranges itself
// for the width it actually has — three columns in the 1076px school page row,
// two in the 628px districts modal. A viewport breakpoint cannot tell those
// apart, and `lg:col-span-4` gave the modal a 126px state select.
// The query container and the grid have to be two separate elements:
// `container-type` makes an element a container for its descendants, not for
// itself, so `@lg:grid-cols-12` on the container element would never match.
const CONTAINER = '@container w-full'
const GRID = 'grid grid-cols-1 items-start gap-x-6 gap-y-4 @lg:grid-cols-12'

// Pairs across the row, narrowing to a third once there is room for three.
const TRIPLE = '@lg:col-span-6 @4xl:col-span-4'
// Pairs across the row and never narrower than a half.
const PAIR = '@lg:col-span-6'

function Grid({children}: {children: React.ReactNode}): React.ReactElement {
  return (
    <div className={CONTAINER}>
      <div className={GRID}>{children}</div>
    </div>
  )
}

export default function AddressForm(props: Props): React.ReactElement {
  const {address, onChangeAddress, isNewAddress, disabled, aside} = props

  const onChangeAddressText = (e: React.ChangeEvent<HTMLTextAreaElement>) => {
    if (e.currentTarget instanceof HTMLTextAreaElement) {
      const target: HTMLTextAreaElement = e.currentTarget
      const formattedAddressLines = target.value.split('\n')
      onChangeAddress({...address, formattedAddressLines})
    } else {
      throw new Error(
        'Expected event.currentTarget to be HTMLTextAreaElement for onChangeAddressText'
      )
    }
  }

  const onChangeCountryCode = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.currentTarget instanceof HTMLSelectElement) {
      const target: HTMLSelectElement = e.currentTarget
      const countryCode = target.value
      const pick = ifUSA(countryCode)

      const {administrativeArea: previousAdministrativeArea, postalCode: previousPostalCode} =
        address

      // For USA, administrative area should be parseable as a US State
      const administrativeArea = pick(
        stateFromAdministrativeArea(previousAdministrativeArea) || '',
        previousAdministrativeArea
      )

      // For USA, postal code should be parseable as a 5-digit zip code
      const postalCode = pick(zipCodeFromPostalCode(previousPostalCode) || '', previousPostalCode)

      onChangeAddress({
        ...address,
        countryCode,
        administrativeArea: administrativeArea as string,
        postalCode: postalCode as string
      })
    } else {
      throw new Error(
        'Expected event.currentTarget to be HTMLSelectElement for onChangeCountryCode'
      )
    }
  }

  const onChangeCity = (city: string) => {
    onChangeAddress({...address, city})
  }

  const onChangeAdministrativeArea = (administrativeArea: string) => {
    onChangeAddress({...address, administrativeArea})
  }

  const onChangePostalCode = (postalCode: string) => {
    onChangeAddress({...address, postalCode})
  }

  const onChangeState = (e: React.ChangeEvent<HTMLSelectElement>) => {
    if (e.currentTarget instanceof HTMLSelectElement) {
      const target: HTMLSelectElement = e.currentTarget
      const administrativeArea = target.value
      onChangeAddress({...address, administrativeArea})
    } else {
      throw new Error('Expected event.currentTarget to be HTMLSelectElement for onChangeState')
    }
  }

  const onChangeZip = (zip: string) => {
    onChangeAddress({...address, postalCode: zip})
  }

  const {countryCode, city, administrativeArea, postalCode, formattedAddressLines} = address

  const placeholders = isNewAddress
    ? getCreatePlaceholders(countryCode)
    : getUpdatePlaceholders(countryCode)

  const countryOptions = sortedCountries.map(country => (
    <option key={country.iso3} value={country.iso3}>
      {country.country}
    </option>
  ))

  const countryField = (span: string) => (
    <FormField
      wrapperStyle={`mb-0 pr-0 ${span}`}
      label="Country"
      htmlFor="address-country"
      required
    >
      <select
        id="address-country"
        className={INPUT_BASE}
        required
        value={countryCode}
        onChange={onChangeCountryCode}
        disabled={disabled}
        data-test="country-input"
      >
        <option key="" value="" />
        {countryOptions}
      </select>
    </FormField>
  )

  const cityField = (span: string) => (
    <FormField wrapperStyle={`mb-0 pr-0 ${span}`} label="City" htmlFor="address-city" required>
      <Input
        id="address-city"
        disabled={disabled}
        value={city}
        type="text"
        required
        placeholder={placeholders.city}
        onChange={onChangeCity}
        dataTest="city-input"
      />
    </FormField>
  )

  const hasAside = aside !== null && aside !== undefined

  const formattedAddressField = (
    <FormField
      wrapperStyle={cn('mb-0 pr-0 @lg:col-span-12', hasAside && '@4xl:col-span-6')}
      label="Formatted Address"
      htmlFor="address-formatted"
      required
    >
      <textarea
        id="address-formatted"
        className={cn(INPUT_BASE, 'resize-y')}
        disabled={disabled}
        rows={4}
        wrap="soft"
        value={mmap(a => a.join('\n'), formattedAddressLines)!}
        required
        placeholder={placeholders.addressText}
        onChange={onChangeAddressText}
        data-test="formatted-address-input"
      />
      <p className="mt-1 text-xs italic text-muted-text">
        Whatever you type above is exactly how the address will appear to users.
      </p>
    </FormField>
  )

  const asideCell = hasAside ? <div className="@lg:col-span-12 @4xl:col-span-6">{aside}</div> : null

  if (countryCode.trim() === '') {
    return (
      <Grid>
        {countryField(PAIR)}
        <p className="text-xs italic text-muted-text @lg:col-span-6 @lg:self-center">
          Select a country to access the remaining address fields.
        </p>
        {/* The aside is not part of the address, so it stays put even before a
            country is chosen. */}
        {asideCell}
      </Grid>
    )
  } else if (countryCode.trim() === 'USA') {
    const stateOptions = usStates.map(({abbreviation, name}) => (
      <option key={abbreviation} value={abbreviation}>
        {abbreviation} - {name}
      </option>
    ))

    return (
      <Grid>
        {countryField(TRIPLE)}
        {cityField(TRIPLE)}
        {/* State and ZIP share one cell, so the row fills instead of leaving
            half of it empty. */}
        <div className={`grid grid-cols-3 gap-x-4 ${TRIPLE}`}>
          <FormField
            wrapperStyle="mb-0 pr-0 col-span-2"
            label="US State"
            htmlFor="address-us-state"
            required
          >
            <select
              id="address-us-state"
              className={INPUT_BASE}
              required
              value={administrativeArea || ''}
              onChange={onChangeState}
              disabled={disabled}
              data-test="us-state-input"
            >
              <option key="" value="" />
              {stateOptions}
            </select>
          </FormField>
          <FormField
            wrapperStyle="mb-0 pr-0 col-span-1"
            label="5-Digit ZIP"
            htmlFor="address-zip"
            required
          >
            <Input
              id="address-zip"
              disabled={disabled}
              value={postalCode || ''}
              type="text"
              placeholder={placeholders.postalCode}
              required
              title="5-digit zip code"
              pattern="[0-9]{5}"
              onChange={onChangeZip}
              dataTest="zip-code-input"
            />
          </FormField>
        </div>
        {formattedAddressField}
        {asideCell}
      </Grid>
    )
  } else {
    return (
      <Grid>
        {countryField(PAIR)}
        {cityField(PAIR)}
        <FormField
          wrapperStyle={`mb-0 pr-0 ${PAIR}`}
          label="Administrative Area (e.g. province)"
          htmlFor="address-administrative-area"
          required
        >
          <Input
            id="address-administrative-area"
            disabled={disabled}
            value={administrativeArea || ''}
            type="text"
            required
            placeholder={placeholders.administrativeArea}
            onChange={onChangeAdministrativeArea}
            dataTest="administrative-area-input"
          />
        </FormField>
        <FormField
          wrapperStyle={`mb-0 pr-0 ${PAIR}`}
          label="Postal Code"
          htmlFor="address-postal-code"
          required
        >
          <Input
            id="address-postal-code"
            disabled={disabled}
            value={postalCode}
            type="text"
            required
            placeholder={placeholders.postalCode}
            onChange={onChangePostalCode}
            dataTest="postal-code-input"
          />
        </FormField>
        {formattedAddressField}
        {asideCell}
      </Grid>
    )
  }
}
